import UAParser from 'ua-parser-js'

import type { ClientRuntime, PortalAPIError } from '@portal/portal-api'

import type { ReportSubmitRequest } from '@/shared/api/crashlog/types'
import { type BrowserStateSnapshot, captureBrowserState } from '@/shared/lib/browser-state'

import { CrashInfoSchema, type SendCrashlogParams, type SendCrashlogV2Params } from './types'

/**
 * How the log of each submit path is packed and compressed. The submitting path knows this; the
 * service cannot tell from the bytes, and the filename a FormData part carries says nothing.
 * `sendCrashlog` and the portal's own logs are base64 of deflate-raw, `sendCrashlogV2` is a zlib
 * stream sent as is.
 */
const SEND_CRASHLOG_FILE = { file_packing: 'base64', file_compression: 'deflate-raw' } as const
const SEND_CRASHLOG_V2_FILE = { file_packing: 'binary', file_compression: 'zlib' } as const

/**
 * Nothing else on a report says which runtime crashed: both clients send the same `apiVersion`,
 * and the rest differs only by absence. A tag is enough, because support already filters by tag.
 */
export function crashlogTags(tags: string, clientRuntime: ClientRuntime): string[] {
    return [...tags.toLowerCase().split(','), `runtime-${clientRuntime}`].filter(Boolean)
}

/** The part of a report that only the submitting path can fill: its log and what the log says. */
type CrashlogReportFields = Pick<
    ReportSubmitRequest,
    'error_message' | 'error_time' | 'file' | 'file_packing' | 'file_compression' | 'info'
>

export function formatCrashlogSubmit(params: SendCrashlogParams): CrashlogReportFields {
    return {
        error_message: params.message,
        error_time: Math.floor(Date.now() / 1000),
        file: params.log,
        ...SEND_CRASHLOG_FILE,
        info: params.crashInfo,
    }
}

export function formatClientCrashlogV2({ crashInfo, logFile }: SendCrashlogV2Params): CrashlogReportFields {
    // Newer clients may send incomplete or unfamiliar errors; still submit the report.
    const error = CrashInfoSchema.shape.error.partial().safeParse(crashInfo.error).data
    const identity = [error?.type, error?.position].filter(Boolean).join('|')

    return {
        // Match Haxe's message format for consistent report and support-ticket titles.
        error_message: [identity, error?.message].filter(Boolean).join(' : '),
        error_time: Math.floor((error?.time ?? Date.now()) / 1000),
        // Forward the client's zlib stream verbatim; compression failure must not block reporting.
        file: logFile ?? '',
        ...SEND_CRASHLOG_V2_FILE,
        info: JSON.stringify(crashInfo),
    }
}

type ConnectFailure = { phase: 'initial' | 'reconnect'; error: PortalAPIError }

/**
 * The crashlog level of a failed Connect. A closed account or an expired session is how the portal
 * works — a tab kept open past the week-long session cookie reconnects without it. A lost network
 * comes and goes. Anything else, a worker or client not found included, is a fault to look into.
 */
function connectFailureLevel(code: PortalAPIError['code']): 'info' | 'warning' | 'error' {
    switch (code) {
        case 'player_disabled':
        case 'account_disabled':
        case 'invalid_session':
        case 'unauthorized':
            return 'info'
        case 'transport_error':
            return 'warning'
        default:
            return 'error'
    }
}

export function formatConnectCrashlog({ phase, error }: ConnectFailure): SendCrashlogParams {
    const type = `Portal|Network|ConnectionFailed|${phase}`
    const userAgent = new UAParser().getResult()

    return {
        log: '',
        message: `${type}: ${error.message}`,
        crashInfo: JSON.stringify({
            // The code stands in for the source position: the crashlog groups reports by type and
            // position, and the message alone mixes causes (`transport_error` reads `Network Error`,
            // `Request aborted`, `timeout exceeded`).
            error: {
                type,
                position: error.code,
                message: error.message,
                // where an exception in the page came from; a request failure carries the axios stack
                scope: error.cause instanceof Error ? { stack: error.cause.stack } : undefined,
            },
            // crashdb indexes info.env.browser; keep this path stable.
            env: { browser: userAgent.browser.name },
            userAgent,
        }),
        tags: [
            connectFailureLevel(error.code),
            'Portal',
            'Network',
            'ConnectionFailed',
            ...(phase === 'reconnect' ? ['reconnect'] : []),
        ].join(','),
    }
}

/** Support filters reports by tag, so the page and connection state has to read as tags too. */
function browserStateTags(browserState: BrowserStateSnapshot): string[] {
    return [
        browserState.page.visibilityState,
        browserState.page.hiddenDurationMs > 0 ? 'was-hidden' : 'never-hidden',
        browserState.connection.online ? 'online' : 'offline',
    ]
}

function mergeTags(tags: string, browserTags: string[]): string {
    return [...new Set([...tags.split(','), ...browserTags])].filter(Boolean).join(',')
}

export function enrichCrashlogWithBrowserState(params: SendCrashlogParams): SendCrashlogParams {
    const browserState = captureBrowserState()
    if (!browserState) {
        return params
    }

    try {
        return {
            ...params,
            crashInfo: JSON.stringify({
                ...JSON.parse(params.crashInfo),
                page: browserState.page,
                connection: browserState.connection,
            }),
            tags: mergeTags(params.tags, browserStateTags(browserState)),
        }
    } catch {
        // Invalid diagnostics must not prevent crash reporting.
        return params
    }
}

/**
 * What the crashing client cannot see: the browser it runs in and the state of the page around it.
 * The Rust client reports the game's own view, so without this a V2 report reaches support with no
 * browser on it at all.
 */
export function enrichCrashlogV2WithBrowserState(params: SendCrashlogV2Params): SendCrashlogV2Params {
    const userAgent = new UAParser().getResult()
    const browserState = captureBrowserState()

    return {
        ...params,
        crashInfo: {
            ...params.crashInfo,
            // crashdb indexes info.env.browser; keep this path stable.
            env: { browser: userAgent.browser.name },
            userAgent,
            // Absent during static rendering; JSON.stringify drops them and the report stays valid.
            page: browserState?.page,
            connection: browserState?.connection,
        },
        tags: browserState ? mergeTags(params.tags, browserStateTags(browserState)) : params.tags,
    }
}
